import { SettingsApi } from '@itsrever/rever-api'
import { apiClient } from '@/infrastructure/api'
import { Layered } from '@/domain/group-settings'
import { GroupReturnConditions } from '@/domain/return-conditions'
import {
  DEFAULT_REFUND_CONFIGURATION,
  RDVSurplusRule,
  RefundConfiguration,
  RefundsSettings
} from '@/domain/refund'
import { ReturnReason, ReturnReasonsSettings } from '@/domain/return-reasons'
import {
  toLayeredRDVSurplus,
  toLayeredRefundConfiguration,
  toLayeredReturnConditions,
  toLayeredReturnReasons,
  toPbRefundConfiguration,
  toPbRDVSurplusRules,
  toPbReturnConditions,
  toGroupReturnConditions,
  toPbReturnReasons
} from './dto'

const settingsApi = new SettingsApi(undefined, undefined, apiClient)

// One group read returns the group's settings and every portal override, like the exchanges one.
export interface GroupSettingsApi<T> {
  find: (group: string, signal?: AbortSignal) => Promise<Layered<T>>
  saveGroup: (group: string, value: T) => Promise<void>
  saveStore: (ecommerceId: string, value: T) => Promise<void>
  resetStore: (ecommerceId: string) => Promise<void>
}

export const returnConditionsApi: GroupSettingsApi<GroupReturnConditions> = {
  find: async (group, signal) =>
    toLayeredReturnConditions(
      (
        await settingsApi.getGroupReturnProcessConditionsV2(
          { ecommerceGroup: group },
          { signal }
        )
      ).data
    ),
  saveGroup: async (group, value) => {
    await settingsApi.saveGroupReturnProcessConditionsV2({
      ecommerceGroup: group,
      pbSaveGroupReturnProcessConditionsV2Request: {
        ecommerce_group: group,
        conditions: toPbReturnConditions(value)
      }
    })
  },
  saveStore: async (ecommerceId, value) => {
    await settingsApi.saveEcommerceReturnProcessConditionsV2({
      ecommerceId,
      pbSaveEcommerceReturnProcessConditionsV2Request: {
        ecommerce_id: ecommerceId,
        conditions: {
          ...toPbReturnConditions(value),
          ecommerce_id: ecommerceId
        }
      }
    })
  },
  resetStore: async (ecommerceId) => {
    await settingsApi.deleteReturnProcessConditionsOverrideV2({ ecommerceId })
  }
}

export const refundConfigurationApi: GroupSettingsApi<RefundConfiguration> = {
  find: async (group, signal) =>
    toLayeredRefundConfiguration(
      (
        await settingsApi.getGroupRefundConfigurationV2(
          { ecommerceGroup: group },
          { signal }
        )
      ).data
    ),
  saveGroup: async (group, value) => {
    await settingsApi.saveGroupRefundConfigurationV2({
      ecommerceGroup: group,
      pbSaveGroupRefundConfigurationV2Request: {
        ecommerce_group: group,
        refund_configuration: toPbRefundConfiguration(value)
      }
    })
  },
  saveStore: async (ecommerceId, value) => {
    await settingsApi.saveEcommerceRefundConfigurationV2({
      ecommerceId,
      pbSaveEcommerceRefundConfigurationV2Request: {
        ecommerce_id: ecommerceId,
        refund_configuration: toPbRefundConfiguration(value)
      }
    })
  },
  resetStore: async (ecommerceId) => {
    await settingsApi.deleteRefundConfigurationOverrideV2({ ecommerceId })
  }
}

export const rdvSurplusApi: GroupSettingsApi<RDVSurplusRule[]> = {
  find: async (group, signal) =>
    toLayeredRDVSurplus(
      (
        await settingsApi.getGroupRDVSurplusV2(
          { ecommerceGroup: group },
          { signal }
        )
      ).data
    ),
  saveGroup: async (group, value) => {
    await settingsApi.saveGroupRDVSurplusV2({
      ecommerceGroup: group,
      pbSaveGroupRDVSurplusV2Request: {
        ecommerce_group: group,
        rdv_surplus: { rules: toPbRDVSurplusRules(value) }
      }
    })
  },
  saveStore: async (ecommerceId, value) => {
    await settingsApi.saveEcommerceRDVSurplusV2({
      ecommerceId,
      pbSaveEcommerceRDVSurplusV2Request: {
        ecommerce_id: ecommerceId,
        rdv_surplus: { rules: toPbRDVSurplusRules(value) }
      }
    })
  },
  resetStore: async (ecommerceId) => {
    await settingsApi.deleteRDVSurplusOverrideV2({ ecommerceId })
  }
}

// Either setting may exist without the other, e.g. before the group's first refund save.
const withSurplus = (
  value: RefundConfiguration | undefined,
  rules: RDVSurplusRule[] | undefined
) =>
  value || rules
    ? { ...(value ?? DEFAULT_REFUND_CONFIGURATION), rdvSurplus: rules ?? [] }
    : undefined

// A portal overriding only one of the two shows as having its own settings, so a group save must not change the
// other: that one is first saved as the portal's own, as the portal gets it now.
async function keepPartialOverrides(group: string) {
  const [refunds, surplus] = await Promise.all([
    refundConfigurationApi.find(group),
    rdvSurplusApi.find(group)
  ])
  const groupRefunds = refunds.group
  const groupSurplus = surplus.group
  await Promise.all([
    ...(groupRefunds
      ? Object.keys(surplus.overrides)
          .filter((store) => !refunds.overrides[store])
          .map((store) => refundConfigurationApi.saveStore(store, groupRefunds))
      : []),
    ...(groupSurplus
      ? Object.keys(refunds.overrides)
          .filter((store) => !surplus.overrides[store])
          .map((store) => rdvSurplusApi.saveStore(store, groupSurplus))
      : [])
  ])
}

// The refund configuration with the RDV surplus rules, edited and locked together in the Refunds tab: a portal
// that overrides either one shows as unlocked, and saving or resetting it writes both. Until the migration writes
// a group's RDV surplus rows, settings-api serves each store's own rules, so the tab leaves them out: an empty list
// saved here would replace them.
export const refundsSettingsApi: GroupSettingsApi<RefundsSettings> = {
  find: async (group, signal) => {
    const [refunds, surplus] = await Promise.all([
      refundConfigurationApi.find(group, signal),
      rdvSurplusApi.find(group, signal)
    ])
    if (!surplus.group && Object.keys(surplus.overrides).length === 0)
      return refunds
    const stores = new Set([
      ...Object.keys(refunds.overrides),
      ...Object.keys(surplus.overrides)
    ])
    return {
      group: withSurplus(refunds.group, surplus.group),
      overrides: Object.fromEntries(
        [...stores].flatMap((store) => {
          const value = withSurplus(
            refunds.overrides[store] ?? refunds.group,
            surplus.overrides[store] ?? surplus.group
          )
          return value ? [[store, value]] : []
        })
      )
    }
  },
  saveGroup: async (group, { rdvSurplus, ...refunds }) => {
    if (!rdvSurplus) return refundConfigurationApi.saveGroup(group, refunds)
    await keepPartialOverrides(group)
    await refundConfigurationApi.saveGroup(group, refunds)
    await rdvSurplusApi.saveGroup(group, rdvSurplus)
  },
  saveStore: async (ecommerceId, { rdvSurplus, ...refunds }) => {
    await refundConfigurationApi.saveStore(ecommerceId, refunds)
    if (rdvSurplus) await rdvSurplusApi.saveStore(ecommerceId, rdvSurplus)
  },
  // settings-api refuses to reset a portal whose group has no rules to follow, so only an existing override goes.
  resetStore: async (ecommerceId) => {
    await refundConfigurationApi.resetStore(ecommerceId)
    const { data } = await settingsApi.getEcommerceRDVSurplusV2({ ecommerceId })
    if (data.overridden) await rdvSurplusApi.resetStore(ecommerceId)
  }
}

export const returnReasonsApi: GroupSettingsApi<ReturnReason[]> = {
  find: async (group, signal) =>
    toLayeredReturnReasons(
      (
        await settingsApi.getGroupReturnReasonsV2(
          { ecommerceGroup: group },
          { signal }
        )
      ).data
    ),
  saveGroup: async (group, value) => {
    await settingsApi.saveGroupReturnReasonsV2({
      ecommerceGroup: group,
      pbSaveGroupReturnReasonsV2Request: {
        ecommerce_group: group,
        return_reasons: toPbReturnReasons(value)
      }
    })
  },
  saveStore: async (ecommerceId, value) => {
    await settingsApi.saveEcommerceReturnReasonsV2({
      ecommerceId,
      pbSaveEcommerceReturnReasonsV2Request: {
        ecommerce_id: ecommerceId,
        return_reasons: toPbReturnReasons(value)
      }
    })
  },
  resetStore: async (ecommerceId) => {
    await settingsApi.deleteReturnReasonsOverrideV2({ ecommerceId })
  }
}

const randomizeOf = (conditions?: GroupReturnConditions) =>
  conditions?.randomizeReturnReasonsOrder ?? false

// The reasons with their order setting, which lives in the return conditions; a save writes both.
// Without group return conditions on v2 there is no order setting to edit here, so it is left out.
export const returnReasonsSettingsApi = (
  conditionsLive: boolean
): GroupSettingsApi<ReturnReasonsSettings> => ({
  find: async (group, signal) => {
    const [reasons, conditions] = await Promise.all([
      returnReasonsApi.find(group, signal),
      conditionsLive ? returnConditionsApi.find(group, signal) : undefined
    ])
    const groupConditions = conditions?.group
    const orderOf = (ecommerceId?: string) =>
      groupConditions
        ? randomizeOf(
            (ecommerceId && conditions.overrides[ecommerceId]) ||
              groupConditions
          )
        : undefined
    return {
      group: reasons.group && {
        reasons: reasons.group,
        randomizeOrder: orderOf()
      },
      overrides: Object.fromEntries(
        Object.entries(reasons.overrides).map(([ecommerceId, value]) => [
          ecommerceId,
          { reasons: value, randomizeOrder: orderOf(ecommerceId) }
        ])
      ),
      // A portal following the group's reasons can still order them its own way.
      portals: Object.fromEntries(
        Object.keys(conditions?.overrides ?? {}).map((ecommerceId) => [
          ecommerceId,
          { randomizeOrder: orderOf(ecommerceId) }
        ])
      )
    }
  },
  saveGroup: async (group, value) => {
    const conditions =
      value.randomizeOrder === undefined
        ? undefined
        : (await returnConditionsApi.find(group)).group
    if (conditions && randomizeOf(conditions) !== value.randomizeOrder)
      await returnConditionsApi.saveGroup(group, {
        ...conditions,
        randomizeReturnReasonsOrder: Boolean(value.randomizeOrder)
      })
    await returnReasonsApi.saveGroup(group, value.reasons)
  },
  saveStore: async (ecommerceId, value) => {
    const conditions =
      value.randomizeOrder === undefined
        ? undefined
        : await findEcommerceConditions(ecommerceId)
    if (conditions && randomizeOf(conditions.value) !== value.randomizeOrder)
      await returnConditionsApi.saveStore(ecommerceId, {
        ...conditions.value,
        randomizeReturnReasonsOrder: Boolean(value.randomizeOrder)
      })
    await returnReasonsApi.saveStore(ecommerceId, value.reasons)
  },
  // Following the group's reasons again includes their order, so a portal's own order goes back to
  // the group's while the rest of its return conditions stay.
  resetStore: async (ecommerceId) => {
    await returnReasonsApi.resetStore(ecommerceId)
    const conditions = conditionsLive
      ? await findEcommerceConditions(ecommerceId)
      : undefined
    if (!conditions?.overridden) return
    const group = (await returnConditionsApi.find(conditions.group)).group
    if (group && randomizeOf(group) !== randomizeOf(conditions.value))
      await returnConditionsApi.saveStore(ecommerceId, {
        ...conditions.value,
        randomizeReturnReasonsOrder: randomizeOf(group)
      })
  }
})

async function findEcommerceConditions(ecommerceId: string) {
  const { data } = await settingsApi.getEcommerceReturnProcessConditionsV2({
    ecommerceId
  })
  return data.conditions
    ? {
        value: toGroupReturnConditions(data.conditions),
        group: data.ecommerce_group ?? '',
        overridden: data.overridden ?? false
      }
    : undefined
}

const findMarkUnreviewed = async (ecommerceId: string, signal?: AbortSignal) =>
  (await settingsApi.findEcommerce({ ecommerceId }, { signal })).data
    .mark_unreviewed_items_as_missing ?? false

// SaveEcommerce replaces the whole portal record, so the stored one goes back with only this changed.
async function saveMarkUnreviewed(ecommerceId: string, value: boolean) {
  const current = (await settingsApi.findEcommerce({ ecommerceId })).data
  if ((current.mark_unreviewed_items_as_missing ?? false) === value) return
  await settingsApi.saveEcommerce({
    pbSaveEcommerceReq: {
      ...current,
      ecommerce_id: ecommerceId,
      mark_unreviewed_items_as_missing: value
    }
  })
}

// The return conditions with the portal setting the backoffice edits beside them. The group shows it on
// when every portal following the group has it, and saving the group sets it on those portals only.
export const returnConditionsWithPortalsApi = (
  stores: string[]
): GroupSettingsApi<GroupReturnConditions> => {
  const followers = (overrides: Record<string, unknown>) =>
    stores.filter((store) => !overrides[store])
  const groupMark = async (portals: string[]) =>
    portals.length > 0 &&
    (await Promise.all(portals.map((p) => findMarkUnreviewed(p)))).every(
      Boolean
    )
  return {
    find: async (group, signal) => {
      const [layered, marks] = await Promise.all([
        returnConditionsApi.find(group, signal),
        Promise.all(stores.map((store) => findMarkUnreviewed(store, signal)))
      ])
      const markOf = Object.fromEntries(
        stores.map((store, i) => [store, marks[i]])
      )
      const following = followers(layered.overrides)
      return {
        group: layered.group && {
          ...layered.group,
          markUnreviewedItemsAsMissing:
            following.length > 0 && following.every((store) => markOf[store])
        },
        overrides: Object.fromEntries(
          Object.entries(layered.overrides).map(([ecommerceId, value]) => [
            ecommerceId,
            {
              ...value,
              markUnreviewedItemsAsMissing: markOf[ecommerceId] ?? false
            }
          ])
        ),
        portals: Object.fromEntries(
          stores.map((store) => [
            store,
            { markUnreviewedItemsAsMissing: markOf[store] ?? false }
          ])
        )
      }
    },
    saveGroup: async (group, value) => {
      await returnConditionsApi.saveGroup(group, value)
      const following = followers(
        (await returnConditionsApi.find(group)).overrides
      )
      // Only a change to the group's switch is applied, so an unrelated save leaves each portal's own.
      if (value.markUnreviewedItemsAsMissing === (await groupMark(following)))
        return
      await Promise.all(
        following.map((store) =>
          saveMarkUnreviewed(store, value.markUnreviewedItemsAsMissing)
        )
      )
    },
    saveStore: async (ecommerceId, value) => {
      await returnConditionsApi.saveStore(ecommerceId, value)
      await saveMarkUnreviewed(ecommerceId, value.markUnreviewedItemsAsMissing)
    },
    // A portal back on the group takes the group's switch, when other portals follow the group to show it.
    resetStore: async (ecommerceId) => {
      const conditions = await findEcommerceConditions(ecommerceId)
      const others = conditions
        ? followers(
            (await returnConditionsApi.find(conditions.group)).overrides
          ).filter((store) => store !== ecommerceId)
        : []
      const mark = await groupMark(others)
      await returnConditionsApi.resetStore(ecommerceId)
      if (others.length > 0) await saveMarkUnreviewed(ecommerceId, mark)
    }
  }
}
